import { createTransform } from 'redux-persist'

// The legacy JS reducers give `combineReducers` nothing to infer from, so the slice type
// is spelled out here rather than indexed off the root state.
type PersistedWaitingRoom = Record<string, unknown> & {
  inVisitRoom?: boolean
  videoCallTimeout?: string | null
  videoCallTimeoutReceivedAtMs?: number | null
}

/**
 * `inVisitRoom` is live call state, not durable state, and it must not survive a reload.
 *
 * `waitingRoomReducer` is persisted (it is not in the store's persist blacklist), so a tab
 * that reloaded after a crash mid-visit came back with `inVisitRoom: true` on whatever
 * route it landed on. `AppLogout` mounts `useInVisitKeepAlive` app-wide, so that stale flag
 * armed a 120-second timer that rewrote the origin-shared `lastUserAction` forever — and
 * because every tab reads the same key, the 30-minute idle logoff could never fire for
 * ANY tab. It also made `WaitingRoom.tsx:169` refuse to start a legitimate visit with
 * "Your visit has been started in different browser tab/window".
 *
 * Stripping only this one key, rather than blacklisting the whole slice, is deliberate:
 * `waitingRoomReducer` also holds `providerProfile`, and `App.tsx:504` gates the MQTT
 * connection on `providerProfile?.ProviderId`. Dropping the slice would delay or break
 * MQTT on every cold load.
 *
 * Nothing depends on this flag persisting: `VisitCallRoom.tsx:905-910` re-dispatches
 * `setVisitRoom(true)` whenever the visit room mounts with it false, so a genuine
 * in-visit reload re-arms itself.
 *
 * The outbound (rehydrate) half is what actually fixes existing users, whose localStorage
 * already contains `inVisitRoom: true`; the inbound half stops writing it.
 *
 * Lives in its own module so it is testable without evaluating `store.ts`, which runs
 * `persistStore`, `initMessageListener` and the state-sync middleware at import time.
 *
 * `videoCallTimeout` / `videoCallTimeoutReceivedAtMs` are stripped for the same reason, and the
 * name is now a slight misnomer (QMD-4640). A rehydrated expiry describes a call that ended
 * whenever the tab died, so honouring it made `useCallTimedOut` declare an immediate timeout on
 * page load — tearing down a call that no longer existed and issuing a stale video DELETE. The
 * pair must always be stripped together: a window measured from one attempt's expiry and
 * another's receipt time is meaningless.
 */
/**
 * The keys this transform forces, in both directions. Declared once so the two directions are
 * identical by construction — a key added to only one of them would be a silent one-way leak,
 * and nothing but a test would catch it.
 */
const EPHEMERAL_CALL_STATE = {
  inVisitRoom: false,
  videoCallTimeout: null,
  videoCallTimeoutReceivedAtMs: null,
} satisfies PersistedWaitingRoom

const strip = (state: PersistedWaitingRoom): PersistedWaitingRoom => ({ ...state, ...EPHEMERAL_CALL_STATE })

export const stripInVisitRoom = createTransform<PersistedWaitingRoom, PersistedWaitingRoom>(strip, strip, {
  whitelist: ['waitingRoomReducer'],
})
